import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Mail, Phone, MapPin, Briefcase, FileText, Calendar,
    Home, Eye, MessageSquare, Building, ArrowRight, AlertCircle, Shield
} from 'lucide-react';
import { useAuth } from '../../../App';
import landlordPropertyService from '../../../services/landlordPropertyService';

/**
 * ManagerProfile Component
 * Read-only profile overview for property managers, backed by the
 * authenticated session and the manager's real property portfolio.
 */
const ManagerProfile = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadPortfolio = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await landlordPropertyService.getAllProperties();
                setProperties(data.properties || []);
            } catch (err) {
                setError(err.message || 'Failed to load portfolio');
            } finally {
                setLoading(false);
            }
        };
        loadPortfolio();
    }, []);

    const stats = {
        total: properties.length,
        active: properties.filter(p => (p.status || p.listing_status) === 'active').length,
        totalViews: properties.reduce((sum, p) => sum + (p.viewCount || p.views_count || 0), 0),
        totalInquiries: properties.reduce((sum, p) => sum + (p.inquiryCount || p.inquiries_count || 0), 0)
    };

    const fullName = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.name || 'Property Manager';

    const initials = (user?.firstName?.[0] || fullName[0] || 'M').toUpperCase();

    const InfoRow = ({ icon: Icon, label, value }) => (
        <div className="flex items-start space-x-3 py-3">
            <div className="flex-shrink-0 p-2 bg-soil-100 rounded-lg">
                <Icon className="h-4 w-4 text-soil-600" />
            </div>
            <div>
                <p className="text-xs text-soil-400">{label}</p>
                <p className="text-sm font-medium text-soil-900">{value || 'Not provided'}</p>
            </div>
        </div>
    );

    const StatCard = ({ label, value, icon: Icon, iconBg, iconColor }) => (
        <div className="bg-white rounded-lg shadow-sm border border-soil-100 p-5">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs font-medium text-soil-600">{label}</p>
                    <p className="font-mono text-2xl font-bold text-soil-900 mt-1">{value}</p>
                </div>
                <div className={`p-2.5 ${iconBg} rounded-lg`}>
                    <Icon className={`h-5 w-5 ${iconColor}`} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Profile Header */}
                <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-8 mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                        <div className="flex items-center space-x-4">
                            <div className="h-16 w-16 rounded-full bg-highland-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0 font-display">
                                {initials}
                            </div>
                            <div>
                                <h1 className="font-display text-2xl font-bold text-soil-900">{fullName}</h1>
                                <div className="flex items-center mt-1 text-sm text-soil-600">
                                    <Shield className="h-4 w-4 mr-1.5 text-highland-500" />
                                    <span className="capitalize">{user?.role || 'manager'}</span>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/manager/settings')}
                            className="px-5 py-2.5 bg-soil-100 text-soil-600 rounded-lg hover:bg-soil-100/70 transition-colors text-sm font-medium"
                        >
                            Account Settings
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Contact & Business Info */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-6">
                            <h2 className="font-display text-base font-semibold text-soil-900 mb-2">Contact Information</h2>
                            <div className="divide-y divide-soil-100">
                                <InfoRow icon={Mail} label="Email" value={user?.email} />
                                <InfoRow icon={Phone} label="Phone" value={user?.phone} />
                                <InfoRow icon={MapPin} label="Location" value={user?.location} />
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-6">
                            <h2 className="font-display text-base font-semibold text-soil-900 mb-2">Business Information</h2>
                            <div className="divide-y divide-soil-100">
                                <InfoRow icon={Briefcase} label="Company" value={user?.company} />
                                <InfoRow icon={FileText} label="Business License" value={user?.businessLicense} />
                            </div>
                        </div>
                    </div>

                    {/* Portfolio Overview */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="font-display text-lg font-semibold text-soil-900">Portfolio Overview</h2>
                            <button
                                onClick={() => navigate('/landlord/properties')}
                                className="flex items-center text-highland-500 hover:text-highland-600 text-sm font-medium"
                            >
                                Manage Properties
                                <ArrowRight className="h-4 w-4 ml-1" />
                            </button>
                        </div>

                        {error && (
                            <div className="bg-murram-100/40 border border-murram-100 rounded-lg p-4 flex items-center space-x-3">
                                <AlertCircle className="h-5 w-5 text-murram-500 flex-shrink-0" />
                                <p className="text-sm text-murram-600">{error}</p>
                            </div>
                        )}

                        {loading ? (
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="bg-white rounded-lg shadow-sm border border-soil-100 p-5 h-24 animate-pulse" />
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <StatCard label="Total Properties" value={stats.total} icon={Building} iconBg="bg-highland-50" iconColor="text-highland-500" />
                                <StatCard label="Active Listings" value={stats.active} icon={Home} iconBg="bg-highland-50" iconColor="text-highland-600" />
                                <StatCard label="Total Views" value={stats.totalViews} icon={Eye} iconBg="bg-jacaranda-100" iconColor="text-jacaranda-500" />
                                <StatCard label="Inquiries" value={stats.totalInquiries} icon={MessageSquare} iconBg="bg-dawn-100/40" iconColor="text-dawn-600" />
                            </div>
                        )}

                        <div className="bg-white rounded-xl shadow-sm border border-soil-100 p-6">
                            <h3 className="font-display text-base font-semibold text-soil-900 mb-2">Account</h3>
                            <InfoRow
                                icon={Calendar}
                                label="Member Since"
                                value={user?.memberSince || user?.createdAt
                                    ? new Date(user.memberSince || user.createdAt).toLocaleDateString('en-US', {
                                        month: 'long',
                                        year: 'numeric'
                                    })
                                    : null}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManagerProfile;
